import { CSSProperties, useMemo } from "react";
import { getColorByRuleId } from "common/api/color-rule";
import { getColumn } from "common/entities";
import type { EntityColumn } from "common/entities/entity-column/types";
import { getColorShade, stringifyRgb } from "common/functions/colors";
import {
  isSelectExpression,
  isSelectField,
  isSummaryField,
} from "common/query/types";
import type { Context } from "common/types/context";
import { SchedulerFilter } from "common/types/scheduler-filters";
import { DEFAULT_EVENT_COLOR as DEFAULT_COLOR } from "x/scheduler2/consts";
import { getRawEventData } from "./functions/event";
import { defaultWOSelect } from "./functions/query";
import { WorkOrderTitle } from "./title";
import type { OnWorkOrderViewClick, WorkOrder } from "./types";

interface PropTypes {
  workOrder: WorkOrder;
  draggable: boolean;
  context: Context;
  filterQuery: SchedulerFilter;
  searchValue: string;
  onClick: OnWorkOrderViewClick;
}

export function WorkOrderTile({
  workOrder,
  draggable,
  context,
  filterQuery,
  searchValue,
  onClick,
}: PropTypes) {
  const { entities } = context;
  const selectedFields = filterQuery?.query?.select || defaultWOSelect;
  const entity = entities[workOrder?.SYSTEM_entityName];
  const woContext = context.site.isGroup
    ? context.createContextForSiteWithFallback(workOrder.site)
    : context;

  const titleFields = useMemo(() => {
    if (!workOrder) return null;

    return selectedFields.map((field, index) => {
      let value: any;
      let label: string | undefined;
      let column: EntityColumn | undefined;

      if (isSelectField(field)) {
        value = workOrder[field.alias || field.name];
        label = field.alias;
        column = getColumn(entity, field.name);
      } else if (isSelectExpression(field)) {
        value = workOrder[field.alias];
        label = field.alias;
      } else if (isSummaryField(field)) {
        value = workOrder[field.entityName];
        label = field.entityName;
      }

      return (
        <WorkOrderTitle
          key={index}
          context={woContext}
          column={column}
          label={label}
          value={value}
          entityName={workOrder.SYSTEM_entityName}
          searchValue={searchValue}
        />
      );
    });
  }, [
    selectedFields,
    workOrder,
    woContext,
    entity,
    searchValue,
    // note: consider carefully which dependencies truly change
  ]);

  const getDynamicStyle = useMemo((): CSSProperties => {
    const ruleColor =
      getColorByRuleId(entity?.colorRules, workOrder?.entityColorRuleId) ??
      DEFAULT_COLOR;

    return {
      borderColor: stringifyRgb(ruleColor),
      // Add a CSS Custom Property for dynamic hover color based on ruleColor
      ["--hover-color" as any]: stringifyRgb(getColorShade(ruleColor, -0.15)),
    };
  }, [context, workOrder]);

  const handleClick = () => {
    onClick(workOrder);
  };

  const eventData = JSON.stringify(getRawEventData(workOrder));

  if (!workOrder?.SYSTEM_id) return null;

  return (
    <div
      className={`x-scheduler-panel-tile qa-scheduler-panel-tile ${
        draggable ? "x-draggable" : ""
      }`}
      data-testid="work-order-tile"
      data-event={eventData}
      style={getDynamicStyle}
    >
      <div className="x-tile-info x-padding-10 x-margin-left-5">
        {titleFields}
        {draggable && <i className="fa fa-ellipsis-v" />}
      </div>
      {onClick && (
        <button
          type="button"
          className="x-tile-clickable"
          onClick={handleClick}
        >
          {_("view")}
        </button>
      )}
    </div>
  );
}
